import type { MembershipPlansPreviewConfig } from '@provision/membership-plans-ui';
import {
  DEFAULT_BRANDING_VALUES,
  resolvePlatformSlaId,
} from 'src/pages/Settings/memberships/components/plans-tab/form-values';
import {
  type PlansTabFormValues,
  type PlansTabPlanValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';

/**
 * Normalizes branding to primary/secondary only with defaults; returns null if branding is null/undefined.
 */
export function fromBranding(
  branding:
    | {
        primaryColor?: string;
        secondaryColor?: string;
      }
    | null
    | undefined,
): { primaryColor: string; secondaryColor: string } | null {
  return branding
    ? {
        primaryColor: branding.primaryColor ?? DEFAULT_BRANDING_VALUES.primaryColor,
        secondaryColor: branding.secondaryColor ?? DEFAULT_BRANDING_VALUES.secondaryColor,
      }
    : null;
}

function priceStringToCents(value: string): number {
  if (!value?.trim()) return 0;
  const num = Number(value.trim());
  return Number.isFinite(num) ? Math.round(num * 100) : 0;
}

/**
 * Maps portal membership form values to the shared preview config shape
 * so the activation flow preview can render from current (possibly unsaved) form state.
 */
export function formValuesToPreviewConfig(
  formValues: PlansTabFormValues | null | undefined,
): MembershipPlansPreviewConfig | null {
  if (!formValues?.plans?.length) return null;

  const enabledPlans = formValues.plans
    .filter((p) => p.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const mostPopularPlan = enabledPlans.find((p) => p.mostPopular);
  const mostPopularProductId = enabledPlans.length ? (mostPopularPlan?.id ?? null) : null;

  const products: MembershipPlansPreviewConfig['products'] = enabledPlans.map(
    (plan: PlansTabPlanValues) => {
      const monthlyCents = priceStringToCents(plan.monthlyPrice);
      const annualCents = priceStringToCents(plan.annualPrice);
      const activeFeatures = (formValues.features ?? [])
        .filter((feature) => feature.includedPlanIds.includes(plan.id))
        .map((feature) => ({
          membershipFeatureId: feature.id,
          overrideContent: feature.overrideContentByPlanId[plan.id] || undefined,
          overrideTooltip: feature.overrideTooltipByPlanId[plan.id] || undefined,
        }));

      return {
        id: plan.id,
        name: plan.name,
        platformSlaId: resolvePlatformSlaId(plan.platformSlaId),
        callForPricing: plan.callForPricing,
        prices: {
          monthly: {
            id: `${plan.id}-monthly`,
            amountCents: monthlyCents,
          },
          ...(formValues.billingOptions.annualEnabled && annualCents >= 0
            ? {
                annual: {
                  id: `${plan.id}-annual`,
                  amountCents: annualCents,
                },
              }
            : {}),
        },
        activeFeatures,
      };
    },
  );

  const features: MembershipPlansPreviewConfig['features'] = (formValues.features ?? [])
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((f) => ({
      membershipFeatureId: f.id,
      sortOrder: f.sortOrder,
      defaultContent: f.defaultContent ?? '',
      defaultTooltip: f.defaultTooltip ?? '',
    }));

  const options: MembershipPlansPreviewConfig['options'] = {
    ...(mostPopularProductId ? { mostPopularProductId } : {}),
    showMonthlyPrices: formValues.billingOptions.monthlyEnabled,
    showAnnualPrices: formValues.billingOptions.annualEnabled,
    showFaq: formValues.options.showFaq,
    faqQuestions: formValues.options.faqQuestions.map((q) => ({
      question: q.question,
      answer: q.answer,
    })),
    showContactSupport: formValues.options.showContactSupport,
    contactSupportLink: formValues.options.contactSupportLink || undefined,
    setupCheckoutText: formValues.options.setupCheckoutText?.trim() || undefined,
    isSetupMode: Boolean(formValues.options.setupCheckoutText?.trim()),
  };

  return {
    options,
    features,
    products,
  };
}
